import {
  TaskEither,
  Either,
  isLeft,
  left,
  right,
  isRight,
  taskEither,
  fetchData,
} from '@shared-commons/utils';
import { ErrorMessage } from '@/shared/utils';

import {
  CreateProfilePayload,
  GetInstrumentsResponse,
  SkillLevelsArraySchema,
  StudySessionsOptions,
} from '../types';
import { getInstrumentsAdapter } from './instruments-adapter';
import { getInstrumentsValidator } from './instruments-validator';
import { studySessionsValidator } from './study-sessions-validator';
import { studySessionsAdapter } from './study-sessions-adapter';
import { skillLevelsValidator } from './skill-levels-validator';
import { skillLevelsAdapter } from './skill-levels-adapter';
import { createProfileAdapter } from './create-profile-adapter';
import { createProfileValidator } from './create-profile-validator';

// ***** 3. createProfile col "fetchData"
export const getInstruments = (token: string): TaskEither<Error, GetInstrumentsResponse> => {
  /**
   * fetchData vuole:
   *  - url
   *  - adapter
   *  - validator
   *  - method (es. 'POST')
   *  - headers
   *  - body
   */
  return fetchData<GetInstrumentsResponse>(
    `${process.env.NEXT_PUBLIC_HOST}/api/profile/instruments`,
    getInstrumentsAdapter,
    getInstrumentsValidator,
    'GET',
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  );
};

export const getStudySessionsOptions = (
  accessToken: string | null,
): TaskEither<Error, StudySessionsOptions> => {
  const validateStudySessionsOptions = (
    studySessions: StudySessionsOptions,
  ): Either<Error, StudySessionsOptions> => {
    const validatedStudySessionsOptions = studySessionsValidator(studySessions);
    if (isLeft(validatedStudySessionsOptions)) {
      const errorMessage = new Error(validatedStudySessionsOptions.value.message);
      return left(new Error(ErrorMessage('Validation error') + errorMessage));
    }
    return right(validatedStudySessionsOptions.value);
  };

  const adapter = (input: StudySessionsOptions): TaskEither<Error, StudySessionsOptions> => {
    const adaptTask: TaskEither<Error, StudySessionsOptions> = async () => {
      try {
        const adaptedStudySessionsOptions = await studySessionsAdapter(input)();
        if (isRight(adaptedStudySessionsOptions)) {
          return right(adaptedStudySessionsOptions.value as StudySessionsOptions);
        } else {
          throw new Error(adaptedStudySessionsOptions.value.message);
        }
      } catch (error) {
        return left(
          new Error(ErrorMessage('Failed to adapt users') + ': ' + (error as Error).message),
        );
      }
    };

    return taskEither(adaptTask);
  };

  return fetchData<StudySessionsOptions>(
    `${process.env.NEXT_PUBLIC_HOST}/api/profile/study-sessions`,
    (input) => adapter(input),
    validateStudySessionsOptions,
    'GET',
    {
      Authorization: `Bearer ${accessToken}`,
    },
  );
};

export const getSkillLevelsOptions = (
  accessToken: string | null,
): TaskEither<Error, SkillLevelsArraySchema> => {
  const validateInstrumentOptions = (
    studySessions: SkillLevelsArraySchema,
  ): Either<Error, SkillLevelsArraySchema> => {
    const validatedInstrumentOptions = skillLevelsValidator(studySessions);
    if (isLeft(validatedInstrumentOptions)) {
      const errorMessage = new Error(validatedInstrumentOptions.value.message);
      return left(new Error(ErrorMessage('Validation error') + errorMessage));
    }
    return right(validatedInstrumentOptions.value);
  };

  const adapter = (input: SkillLevelsArraySchema): TaskEither<Error, SkillLevelsArraySchema> => {
    const adaptTask: TaskEither<Error, SkillLevelsArraySchema> = async () => {
      //TODO @Leo To fix the type error, you need to cast the value of the adaptedInstrumentOptions to the expected type.

      try {
        const adaptedInstrumentOptions = await skillLevelsAdapter(input)();
        if (isRight(adaptedInstrumentOptions)) {
          return right(adaptedInstrumentOptions.value as SkillLevelsArraySchema);
        } else {
          throw new Error(adaptedInstrumentOptions.value.message);
        }
      } catch (error) {
        return left(
          new Error(ErrorMessage('Failed to adapt users') + ': ' + (error as Error).message),
        );
      }
    };

    return taskEither(adaptTask);
  };

  return fetchData<SkillLevelsArraySchema>(
    `${process.env.NEXT_PUBLIC_HOST}/api/profile/skill-levels/get`,
    (input) => adapter(input),
    validateInstrumentOptions,
    'GET',
    {
      Authorization: `Bearer ${accessToken}`,
    },
  );
};

export const createProfile = (
  body: CreateProfilePayload,
  token: string,
): TaskEither<
  Error,
  {
    message: string;
  }
> => {
  /**
   * fetchData vuole:
   *  - url
   *  - adapter
   *  - validator
   *  - method (es. 'POST')
   *  - headers
   *  - body
   */
  return fetchData<{
    message: string;
  }>(
    `${process.env.NEXT_PUBLIC_HOST}/api/profile/onboarding/create-profile`,
    createProfileAdapter,
    createProfileValidator,
    'POST',
    {
      Authorization: `Bearer ${token}`,
    },
    JSON.stringify({
      ...body,
    }),
  );
};
